// filepath: c:\\Projects\\manaber\\realtime-translation\\admin-portal\\src\\pages\\BasicSetupScreen.tsx
import { FunctionComponent, useCallback, useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminSideMenu from "../components/AdminSideMenu";
import AdminTopMenu from "../components/AdminTopMenu/AdminTopMenu"; // Added import
import { useRooms } from "../contexts/RoomContext";
import styles from "./BasicSetupScreen.module.css";

// Generate random room ID in format "xxx-xxx" (6 lowercase letters separated by dash)
const generateRoomId = (): string => {
  const letters = 'abcdefghijklmnopqrstuvwxyz';
  const getRandomLetter = () => letters.charAt(Math.floor(Math.random() * letters.length));

  return `${getRandomLetter()}${getRandomLetter()}${getRandomLetter()}-${getRandomLetter()}${getRandomLetter()}${getRandomLetter()}`;
};

declare global {
  interface Window {
    google?: any;
  }
}

const BasicSetupScreen: FunctionComponent = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const roomId = id ? parseInt(id, 10) : null;
  const { currentRoom, loading, error, fetchRoomById, addRoom, editRoom, removeRoom, clearError, fetchNextSchedule } = useRooms();
  const [loadingTimeout, setLoadingTimeout] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    address: "",
    city: "", // Added city field
    customRoomId: roomId ? "" : generateRoomId(), // Generate room ID for new rooms, empty for existing
    speakerLanguage: "ar-AE",
    listenersLanguages: roomId ? ["en-US"] : [
      "en-GB", "ur-PK", "ml-IN", "bn-IN", "hi-IN", "zh-CN", "fr-FR", "ru-RU", "de-DE", "es-ES", "ps-AF", "fa-IR", "no-No", "ta-IN", "fil-PH"
    ], // Default listeners languages for new room
    latitude: "",
    longitude: "",
    geoCenterLat: "",
    geoCenterLng: "",
    geoRestricted: false,
    geoRadiusMeters: "",
    geoLabel: "",
    roomType: "room type 1",
    enableTranscription: false,
    showTranslationText: true, // Added show translation text field with default true
    // Access control
    speakerPassword: "",
    listenersPassword: "",
    // Scheduling fields
    isTest: false,
    scheduleActive: roomId ? 0 : 2, // Default to Automatic Friday Sermon for new rooms
    scheduleDayOfWeek: 5, // Default Friday
    scheduleStartTime: "", // HH:MM
    scheduleEndTime: "", // HH:MM
  });

  // Google Places autocomplete (optional)
  const placesApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const [placesReady, setPlacesReady] = useState(false);
  const [placesError, setPlacesError] = useState<string | null>(null);
  const placeSearchInputRef = useRef<HTMLInputElement | null>(null);
  const placesAutocompleteRef = useRef<any>(null);

  // ---- Timezone Conversion Helpers ----
  // Convert a UTC time string (HH:MM or HH:MM:SS) to local time string HH:MM for display
  const utcToLocalTime = (timeStr?: string | null) => {
    if (!timeStr) return "";
    const parts = timeStr.split(":");
    if (parts.length < 2) return "";
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    if (isNaN(hours) || isNaN(minutes)) return "";
    const todayUtc = new Date();
    // Build date at UTC time
    const utcDate = new Date(Date.UTC(todayUtc.getUTCFullYear(), todayUtc.getUTCMonth(), todayUtc.getUTCDate(), hours, minutes, 0));
    const localHours = utcDate.getHours().toString().padStart(2, '0');
    const localMinutes = utcDate.getMinutes().toString().padStart(2, '0');
    return `${localHours}:${localMinutes}`;
  };

  // Convert a local time string HH:MM to UTC HH:MM string for API persistence
  const localToUtcTime = (timeStr?: string | null) => {
    if (!timeStr) return "";
    const parts = timeStr.split(":");
    if (parts.length < 2) return "";
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    if (isNaN(hours) || isNaN(minutes)) return "";
    const todayLocal = new Date();
    const localDate = new Date(todayLocal.getFullYear(), todayLocal.getMonth(), todayLocal.getDate(), hours, minutes, 0);
    const utcHours = localDate.getUTCHours().toString().padStart(2, '0');
    const utcMinutes = localDate.getUTCMinutes().toString().padStart(2, '0');
    return `${utcHours}:${utcMinutes}`;
  };
  const onAdvancedSettingsClick = useCallback(() => {
    navigate(roomId ? `/advanced-setup/${roomId}` : "/advanced-setup");
  }, [navigate, roomId]);

  const onJoinInfoClick = useCallback(() => {
    navigate(roomId ? `/join-info/${roomId}` : "/join-info");
  }, [navigate, roomId]);

  const onCancelClick = useCallback(() => {
    navigate("/my-room-screen"); // Navigate to My Rooms screen on cancel
  }, [navigate]);
  useEffect(() => {
    if (roomId !== null) {
      fetchRoomById(roomId);
      const timeoutId = setTimeout(() => {
        setLoadingTimeout(true);
      }, 10000);
      return () => clearTimeout(timeoutId);
    }
  }, [roomId, fetchRoomById]); useEffect(() => {
    if (currentRoom && roomId !== null) {
      let listenersLanguages;

      // Handle different formats of toLanguages from API
      if (Array.isArray(currentRoom.toLanguages)) {
        listenersLanguages = currentRoom.toLanguages;
      } else if (typeof currentRoom.toLanguages === 'string') {
        try {
          // Try to parse as JSON array
          listenersLanguages = JSON.parse(currentRoom.toLanguages);
          if (!Array.isArray(listenersLanguages)) {
            listenersLanguages = [currentRoom.toLanguages];
          }
        } catch (e) {
          // If parsing fails, treat as single language
          listenersLanguages = [currentRoom.toLanguages];
        }
      } else {
        listenersLanguages = ["en-US"]; // Default fallback
      }

      setFormData({
        name: currentRoom.name || "",
        description: currentRoom.description || "",
        address: currentRoom.address || "",
        city: currentRoom.city || "", // Added city field from API
        customRoomId: currentRoom.roomId || "",
        speakerLanguage: currentRoom.fromLanguage || "ar-AE",
        listenersLanguages: listenersLanguages,
        latitude: currentRoom.lat?.toString() || "",
        longitude: currentRoom.lng?.toString() || "",
        geoCenterLat: currentRoom.geoCenterLat != null ? currentRoom.geoCenterLat.toString() : (currentRoom.lat != null ? currentRoom.lat.toString() : ""),
        geoCenterLng: currentRoom.geoCenterLng != null ? currentRoom.geoCenterLng.toString() : (currentRoom.lng != null ? currentRoom.lng.toString() : ""),
        geoRestricted: currentRoom.geoRestricted ?? false,
        geoRadiusMeters: currentRoom.geoRadiusMeters != null ? currentRoom.geoRadiusMeters.toString() : "",
        geoLabel: (currentRoom as any).geoLabel || "",
        roomType: currentRoom.roomType || "room type 1",
        enableTranscription: currentRoom.enableTranscription || false,
        showTranslationText: currentRoom.showTranslationText !== undefined ? currentRoom.showTranslationText : true, // Added show translation text field from API
        isTest: currentRoom.isTest ?? false,
        speakerPassword: (currentRoom as any).speakerPassword || "",
        listenersPassword: (currentRoom as any).listenersPassword || "",
        scheduleActive: currentRoom.scheduleActive ?? 0,
        scheduleDayOfWeek: currentRoom.scheduleDayOfWeek ?? 5,
        scheduleStartTime: utcToLocalTime(currentRoom.scheduleStartTime),
        scheduleEndTime: utcToLocalTime(currentRoom.scheduleEndTime),
      });
    }
  }, [currentRoom, roomId]);
  const handleInputChange = (field: string, value: string | boolean | string[] | number | null) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      [field]: value,
    }));
  };

  const loadPlacesScript = useCallback((key: string) => {
    return new Promise<void>((resolve, reject) => {
      if (typeof window === "undefined") return reject(new Error("window unavailable"));
      if (window.google?.maps?.places) return resolve();
      const existing = document.getElementById("google-places-script");
      if (existing) {
        existing.addEventListener("load", () => resolve(), { once: true });
        existing.addEventListener("error", () => reject(new Error("Failed to load Google Places")), { once: true });
        return;
      }
      const script = document.createElement("script");
      script.id = "google-places-script";
      script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Failed to load Google Places"));
      document.body.appendChild(script);
    });
  }, []);

  useEffect(() => {
    if (!placesApiKey) return;
    let canceled = false;
    // Reset state on each mount/attempt so a prior transient error doesn't stick
    setPlacesError(null);
    setPlacesReady(false);
    loadPlacesScript(placesApiKey)
      .then(() => {
        if (canceled) return;
        const initAutocomplete = () => {
          if (canceled) return;
          if (!placeSearchInputRef.current || !window.google?.maps?.places) {
            // Give the script a short grace period before declaring failure
            setTimeout(() => {
              if (canceled) return;
              if (!placeSearchInputRef.current || !window.google?.maps?.places) {
                setPlacesError("Places API not available");
              } else {
                initAutocomplete();
              }
            }, 300);
            return;
          }
          const ac = new window.google.maps.places.Autocomplete(placeSearchInputRef.current, {
            fields: ["geometry", "formatted_address", "address_components"],
          });
          placesAutocompleteRef.current = ac;
          ac.addListener("place_changed", () => {
            const place = ac.getPlace();
            const loc = place?.geometry?.location;
            if (loc) {
              const lat = loc.lat();
              const lng = loc.lng();
              handleInputChange("latitude", lat.toFixed(6));
              handleInputChange("longitude", lng.toFixed(6));
            }
            if (place?.formatted_address) {
              handleInputChange("address", place.formatted_address);
            }
            if (place?.address_components) {
              const cityComp = place.address_components.find((c: any) =>
                c.types?.includes("locality") || c.types?.includes("administrative_area_level_1")
              );
              if (cityComp?.long_name) {
                handleInputChange("city", cityComp.long_name);
              }
            }
          });
          setPlacesReady(true);
          setPlacesError(null);
        };
        initAutocomplete();
      })
      .catch((err) => {
        if (!canceled) setPlacesError(err?.message || "Failed to load map search");
      });
    return () => {
      canceled = true;
    };
  }, [handleInputChange, loadPlacesScript, placesApiKey]);

  const [validationError, setValidationError] = useState<string | null>(null);
  const validationErrorRef = useRef<HTMLDivElement | null>(null);
  const roomIdInputRef = useRef<HTMLInputElement | null>(null);

  const handleDeleteRoom = useCallback(async () => {
    if (roomId == null) return;
    const label = currentRoom ? `${currentRoom.name}${currentRoom.roomId ? ` (${currentRoom.roomId})` : ''}` : `Room ID ${roomId}`;
    const ok = window.confirm(`Delete ${label}?\n\nThis cannot be undone.`);
    if (!ok) return;
    try {
      setIsDeleting(true);
      await removeRoom(roomId);
      navigate("/rooms");
    } catch (e) {
      console.error("Failed to delete room:", e);
      setValidationError("Failed to delete room. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  }, [roomId, currentRoom, removeRoom, navigate]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  // Next schedule state (for automatic Friday sermon)
  const [nextScheduleLoading, setNextScheduleLoading] = useState(false);
  const [nextScheduleError, setNextScheduleError] = useState<string | null>(null);
  const [nextScheduleLocal, setNextScheduleLocal] = useState<string | null>(null);
  const [nextScheduleReference, setNextScheduleReference] = useState<any | null>(null);

  const loadNextSchedule = useCallback(async (cityParam: string) => {
    try {
      setNextScheduleLoading(true);
      setNextScheduleError(null);
      setNextScheduleLocal(null);
      setNextScheduleReference(null);
      const data = await fetchNextSchedule(cityParam);
      const utcIso: string | null = data?.nextScheduleUtc || null;
      if (utcIso) {
        const d = new Date(utcIso);
        const localStr = d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
        setNextScheduleLocal(localStr);
      }
      setNextScheduleReference(data?.reference || null);
    } catch (e: any) {
      setNextScheduleError(e.message || 'Failed to load next schedule');
    } finally {
      setNextScheduleLoading(false);
    }
  }, [fetchNextSchedule]);

  // Trigger fetch when scheduleActive switches to 2 or city changes while in mode 2
  useEffect(() => {
    if (formData.scheduleActive === 2 && formData.city.trim()) {
      loadNextSchedule(formData.city.trim());
    } else {
      setNextScheduleLocal(null);
      setNextScheduleReference(null);
      setNextScheduleError(null);
    }
  }, [formData.scheduleActive, formData.city, loadNextSchedule]);

  const handleSubmit = async () => {
    setValidationError(null);
    if (!formData.name.trim()) {
      setValidationError("Room name is required");
      return;
    }

    if (!formData.listenersLanguages || formData.listenersLanguages.length === 0) {
      setValidationError("At least one listener language is required");
      return;
    }

    if (!formData.city.trim()) {
      setValidationError("City is required");
      return;
    }

    if (formData.geoRestricted) {
      const centerLat = formData.latitude;
      const centerLng = formData.longitude;
      if (!centerLat || !centerLng || !formData.geoRadiusMeters) {
        setValidationError("Geo restriction needs room latitude/longitude and radius (meters)");
        return;
      }
    }

    // Scheduling validation
    if (formData.scheduleActive === 1) { // manual
      if (!formData.scheduleStartTime || !formData.scheduleEndTime) {
        setValidationError("Start and end time are required for manual schedule");
        return;
      }
      // Basic HH:MM validation and ordering check
      const start = formData.scheduleStartTime;
      const end = formData.scheduleEndTime;
      const startNum = parseInt(start.replace(':', ''), 10);
      const endNum = parseInt(end.replace(':', ''), 10);
      if (endNum <= startNum) {
        setValidationError("End time must be after start time");
        return;
      }
      if (formData.scheduleDayOfWeek === undefined || formData.scheduleDayOfWeek === null) {
        setValidationError("Day of week is required for manual schedule");
        return;
      }
    }

    try {
      setIsSaving(true);
      const roomData: any = {
        name: formData.name,
        description: formData.description,
        address: formData.address,
        city: formData.city, // Added city field for API
        fromLanguage: formData.speakerLanguage,
        toLanguages: formData.listenersLanguages,
        lat: formData.latitude ? parseFloat(formData.latitude) : null,
        lng: formData.longitude ? parseFloat(formData.longitude) : null,
        roomType: formData.roomType,
        enableTranscription: formData.enableTranscription,
        showTranslationText: formData.showTranslationText, // Added show translation text field for API
        isTest: formData.isTest,
        scheduleActive: formData.scheduleActive,
        scheduleDayOfWeek: formData.scheduleActive === 2 ? 5 : formData.scheduleDayOfWeek, // Force Friday for automatic
      };

      // Geo restriction fields:
      // - If disabled, OMIT geo fields entirely to avoid backend validation errors and unintended overwrites.
      // - If enabled, send required center + radius.
      if (formData.geoRestricted) {
        roomData.geoRestricted = true;
        roomData.geoCenterLat = formData.latitude ? parseFloat(formData.latitude) : undefined;
        roomData.geoCenterLng = formData.longitude ? parseFloat(formData.longitude) : undefined;
        roomData.geoRadiusMeters = formData.geoRadiusMeters ? parseInt(formData.geoRadiusMeters, 10) : undefined;
        roomData.geoLabel = formData.geoLabel ? formData.geoLabel : undefined;
      } else {
        roomData.geoRestricted = false;
      }

      if (formData.scheduleActive === 1) {
        roomData.scheduleStartTime = localToUtcTime(formData.scheduleStartTime as string);
        roomData.scheduleEndTime = localToUtcTime(formData.scheduleEndTime as string);
      }

      if (formData.customRoomId) {
        roomData.roomId = formData.customRoomId;
      }

      // Always include passwords; empty string means public access
      roomData.speakerPassword = (formData.speakerPassword || "");
      roomData.listenersPassword = (formData.listenersPassword || "");

      if (roomId !== null) {
        await editRoom(roomId, roomData);
        navigate(`/advanced-setup/${roomId}`);
      } else {
        const newRoom = await addRoom({
          ...roomData,
          companyId: 1,
        });
        navigate(`/advanced-setup/${newRoom.id}`);
      }
    } catch (error: any) {
      console.error("Failed to save room:", error);
      const backendDetails = error?.response?.data?.details;
      if (Array.isArray(backendDetails) && backendDetails.length > 0) {
        const msgs = backendDetails
          .map((d: any) => d?.msg)
          .filter(Boolean);
        if (msgs.length > 0) {
          setValidationError(msgs.join(" | "));
          return;
        }
      }

      const backendMsg = error?.response?.data?.error || error?.response?.data?.message;
      const status = error?.response?.status;
      const message = backendMsg || error?.message || "Failed to save room. Please try again.";
      setValidationError(message);
      // If duplicate roomId (409), focus Room ID input so user can change it
      if (status === 409) {
        setTimeout(() => {
          roomIdInputRef.current?.focus();
          roomIdInputRef.current?.select?.();
        }, 0);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleGoBack = useCallback(() => {
    clearError();
    navigate("/my-room-screen");
  }, [navigate, clearError]);

  // Scroll into view when validation error appears
  useEffect(() => {
    if (validationError && validationErrorRef.current) {
      validationErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      validationErrorRef.current.focus({ preventScroll: true });
    }
  }, [validationError]);

  return (
    <div>
      <AdminSideMenu />
      <AdminTopMenu />
      <div className={styles.basicSetupScreenWrapper}> {/* Outermost wrapper */}
        <div className={styles.mainContentContainer}> {/* Main content area */}
          <div className={styles.tabsContainer}>
            <div className={`${styles.tabItem} ${styles.activeTab}`}>Basic setup</div>
            <div className={styles.tabItem} onClick={onAdvancedSettingsClick}>Advanced settings</div>
            <div className={styles.tabItem} onClick={onJoinInfoClick}>{`Join Info`}</div>
          </div>

          {loading && !loadingTimeout ? (
            <div className={styles.loadingContainer}>
              <div className="loading-spinner"></div>
              <p>Loading room data...</p>
            </div>
          ) : (loading && loadingTimeout) ? (
            <div className={styles.errorContainer}>
              <p>Loading is taking longer than expected.</p>
              <button onClick={handleGoBack} className={styles.backButton}>
                Back to Rooms
              </button>
            </div>
          ) : validationError ? (
            // Display validation errors within the form area or above it
            <div className={styles.formContainer}> {/* Wrap validation error for consistent width */}
              <div
                ref={validationErrorRef}
                className={styles.errorContainer}
                style={{ width: '100%', marginBottom: '20px' }}
                role="alert"
                tabIndex={-1}
                aria-live="assertive"
              >
                <p>{validationError}</p>
              </div>              {/* Render form fields below validation error */}
              <FormFields formData={formData} handleInputChange={handleInputChange} isSaving={isSaving} handleSubmit={handleSubmit} onCancelClick={onCancelClick} roomId={roomId}
                roomIdInputRef={roomIdInputRef}
                nextScheduleLoading={nextScheduleLoading} nextScheduleError={nextScheduleError} nextScheduleLocal={nextScheduleLocal} nextScheduleReference={nextScheduleReference}
                placeSearchInputRef={placeSearchInputRef} placesApiKey={placesApiKey} placesError={placesError} placesReady={placesReady}
                showDelete={roomId !== null} isDeleting={isDeleting} onDeleteClick={handleDeleteRoom}
              />
            </div>
          ) : error ? (
            <div className={styles.errorContainer}>
              <p>Error: {error}</p>
              <button onClick={handleGoBack} className={styles.backButton}>
                Back to Rooms
              </button>
            </div>
          ) : (
            // Form fields and buttons
            <FormFields formData={formData} handleInputChange={handleInputChange} isSaving={isSaving} handleSubmit={handleSubmit} onCancelClick={onCancelClick} roomId={roomId}
              roomIdInputRef={roomIdInputRef}
              nextScheduleLoading={nextScheduleLoading} nextScheduleError={nextScheduleError} nextScheduleLocal={nextScheduleLocal} nextScheduleReference={nextScheduleReference}
              placeSearchInputRef={placeSearchInputRef} placesApiKey={placesApiKey} placesError={placesError} placesReady={placesReady}
              showDelete={roomId !== null} isDeleting={isDeleting} onDeleteClick={handleDeleteRoom}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Extracted form fields into a sub-component for clarity
  const FormFields: FunctionComponent<{
  formData: any;
  handleInputChange: (field: string, value: string | boolean | string[] | number | null) => void;
  isSaving: boolean;
  handleSubmit: () => void;
  onCancelClick: () => void;
  roomId?: number | null; // Add roomId as optional prop
  roomIdInputRef?: React.RefObject<HTMLInputElement | null>;
  nextScheduleLoading: boolean;
  nextScheduleError: string | null;
  nextScheduleLocal: string | null;
  nextScheduleReference: any | null;
  placeSearchInputRef?: React.RefObject<HTMLInputElement | null>;
  placesApiKey?: string;
  placesError?: string | null;
  placesReady?: boolean;
  showDelete?: boolean;
  isDeleting?: boolean;
  onDeleteClick?: () => void;
}> = ({ formData, handleInputChange, isSaving, handleSubmit, onCancelClick, roomId, roomIdInputRef, nextScheduleLoading, nextScheduleError, nextScheduleLocal, nextScheduleReference, placeSearchInputRef, placesApiKey, placesError, placesReady, showDelete, isDeleting, onDeleteClick }) => {
  // Language options for both speaker and listeners
  const languageOptions = [
    { value: "ar-AE", label: "Arabic (United Arab Emirates) (ar-AE)" },
    { value: "en-US", label: "English (United States) (en-US)" },
    { value: "en-GB", label: "English (United Kingdom) (en-GB)" },
    // { value: "en-AU", label: "English (Australia) (en-AU)" },
    { value: "fr-FR", label: "French (France) (fr-FR)" },
    { value: "fr-CA", label: "French (Canada) (fr-CA)" },
    { value: "es-ES", label: "Spanish (Spain) (es-ES)" },
    { value: "es-US", label: "Spanish (United States) (es-US)" },
    { value: "de-DE", label: "German (Germany) (de-DE)" },
    { value: "it-IT", label: "Italian (Italy) (it-IT)" },
    { value: "pt-BR", label: "Portuguese (Brazil) (pt-BR)" },
    { value: "pt-PT", label: "Portuguese (Portugal) (pt-PT)" },
    { value: "ru-RU", label: "Russian (Russia) (ru-RU)" },
    { value: "ja-JP", label: "Japanese (Japan) (ja-JP)" },
    { value: "ko-KR", label: "Korean (South Korea) (ko-KR)" },
    { value: "zh-CN", label: "Chinese (China) (zh-CN)" },
    // { value: "zh-TW", label: "Chinese (Taiwan) (zh-TW)" },
    { value: "hi-IN", label: "Hindi (India) (hi-IN)" },
    { value: "ur-PK", label: "Urdu (Pakistan) (ur-PK)" },
    { value: "ml-IN", label: "Malayalam (India) (ml-IN)" },
    { value: "ps-AF", label: "Pashto (Afghanistan) (ps-AF)" },
    { value: "fa-IR", label: "Persian (Iran) (fa-IR)" },
    { value: "bn-IN", label: "Bengali (India) (bn-IN)" },
    { value: "no-No", label: "Norwegian (Norway) (no-No)" },
    { value: "ro-RO", label: "Romanian (Romania) (ro-RO)" },
    { value: "ta-IN", label: "Tamil (India) (ta-IN)" },
    { value: "fil-PH", label: "Filipino (Philippines) (fil-PH)" }
  ];
  const [geoStatus, setGeoStatus] = useState<string | null>(null);
  // State for dropdown visibility
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  // Handle multiselect change for listeners languages
  const handleListenersLanguagesChange = (languageCode: string, isChecked: boolean) => {
    let currentLanguages = formData.listenersLanguages || [];

    // Handle case where listenersLanguages is a string representation of an array
    if (typeof currentLanguages === 'string') {
      try {
        currentLanguages = JSON.parse(currentLanguages);
      } catch (e) {
        currentLanguages = [currentLanguages];
      }
    }

    // Ensure it's an array
    if (!Array.isArray(currentLanguages)) {
      currentLanguages = [];
    }

    let updatedLanguages;

    if (isChecked) {
      // Add language if not already present
      updatedLanguages = currentLanguages.includes(languageCode)
        ? currentLanguages
        : [...currentLanguages, languageCode];
    } else {
      // Remove language
      updatedLanguages = currentLanguages.filter((lang: string) => lang !== languageCode);
    }

    handleInputChange("listenersLanguages", updatedLanguages);
  };// Get display text for selected languages
  const getSelectedLanguagesText = () => {
    let selectedLanguages = formData.listenersLanguages || [];

    // Handle case where listenersLanguages is a string representation of an array
    if (typeof selectedLanguages === 'string') {
      try {
        selectedLanguages = JSON.parse(selectedLanguages);
      } catch (e) {
        // If parsing fails, treat as single language
        selectedLanguages = [selectedLanguages];
      }
    }

    // Ensure it's an array
    if (!Array.isArray(selectedLanguages)) {
      selectedLanguages = [];
    }

    if (selectedLanguages.length === 0) {
      return "Select languages...";
    }
    if (selectedLanguages.length === 1) {
      const lang = languageOptions.find(opt => opt.value === selectedLanguages[0]);
      return lang ? lang.label : selectedLanguages[0];
    }
    // Show language codes when multiple languages are selected
    return selectedLanguages.join(", ");
  };

  return (
    <div className={styles.formContainer}>
      <div className={styles.textfiledRoomName}>
        <div className={styles.roomName}>Room Name</div>
        <input
          className={styles.roomNameInput}
          value={formData.name}
          onChange={(e) => handleInputChange("name", e.target.value)}
          placeholder="Enter room name"
        />
      </div>
      <div className={styles.textfiledRoomId}>
        <div className={styles.roomName}>Room ID</div>
        <input
          className={styles.roomIdInput}
          value={formData.customRoomId}
          ref={roomIdInputRef}
          onChange={(e) => handleInputChange("customRoomId", e.target.value)} placeholder={roomId !== null ? "Enter room ID (optional)" : "Auto-generated (you can edit)"}
        />
      </div>

      <div className={styles.textfiledRoomName1}>
        <div className={styles.roomName}>Room Description</div>
        <textarea
          className={styles.roomDescriptionInput}
          value={formData.description}
          onChange={(e) => handleInputChange("description", e.target.value)}
          placeholder="Enter room description"
        />
      </div>

      <div className={styles.dropdownRoomType}> {/* Ensure this class exists or is styled */}
        <div className={styles.roomName}>Room Type</div>
        <div style={{ position: 'relative' }}>
          <select
            className={styles.roomTypeSelect}
            value={formData.roomType}
            onChange={(e) => handleInputChange("roomType", e.target.value)}
          >
            <option value="room type 1">Room Type 1</option>
            <option value="room type 2">Room Type 2</option>
            <option value="room type 3">Room Type 3</option>
            <option value="room type 4">Room Type 4</option>
            <option value="room type 5">Room Type 5</option>
            <option value="room type 6">Room Type 6</option>
          </select>
          <img
            className={styles.arrowDropDownIcon}
            alt=""
            src="/arrow-drop-down.svg"
          />
        </div>
      </div>

      {/* Optional Google Places search (fills address + lat/lng) */}
      <div style={{ marginTop: 12, marginBottom: 8 }}>
        <div className={styles.roomName} style={{ marginBottom: 6 }}>Search place (auto-fills location)</div>
        <input
          ref={placeSearchInputRef}
          type="text"
          className={styles.searchInput}
          placeholder={placesApiKey ? "Type a place name or address" : "Set REACT_APP_GOOGLE_MAPS_API_KEY to enable search"}
          disabled={!placesApiKey || !!placesError}
          style={{ marginBottom: 6, width: "100%" }}
        />
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
          <button
            type="button"
            onClick={() => {
              if (!navigator.geolocation) {
                setGeoStatus("Geolocation not supported in this browser");
                return;
              }
              setGeoStatus("Requesting current location...");
              navigator.geolocation.getCurrentPosition(
                (pos) => {
                  const lat = pos.coords.latitude.toFixed(6);
                  const lng = pos.coords.longitude.toFixed(6);
                  handleInputChange("latitude", lat);
                  handleInputChange("longitude", lng);
                  setGeoStatus(`Location set to ${lat}, ${lng}`);
                },
                (err) => {
                  setGeoStatus(err?.message || "Unable to get current location");
                },
                { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
              );
            }}
            style={{
              padding: "8px 12px",
              borderRadius: 6,
              border: "1px solid #ccc",
              background: "#f2f2f2",
              cursor: "pointer",
              fontSize: 13
            }}
          >
            Use current location
          </button>
          <div style={{ fontSize: 12, color: "#555" }}>{geoStatus || ""}</div>
        </div>
        <div style={{ fontSize: 12, color: placesError ? "#c00" : "#555" }}>
          {placesError
            ? placesError
            : placesApiKey
            ? placesReady
              ? "Powered by Google Places. Selecting a result will set address and lat/lng."
              : "Loading place search..."
            : "Search disabled: missing REACT_APP_GOOGLE_MAPS_API_KEY."}
        </div>
      </div>

      <div className={styles.textfiledRoomLocation}>
        <div className={styles.roomName}>Room Location</div>
        <input
          type="text" // Changed from textarea to input
          className={styles.roomLocationInput}
          value={formData.address}
          onChange={(e) => handleInputChange("address", e.target.value)}
          placeholder="Enter room address"
        />
      </div>

      <div className={styles.textfiledRoomCity}>
        <div className={styles.roomName}>City</div>
        <input
          type="text"
          className={styles.roomCityInput}
          value={formData.city}
          onChange={(e) => {
            const value = e.target.value;
            // Limit to 100 characters
            if (value.length <= 100) {
              handleInputChange("city", value);
            }
          }}
          placeholder="Enter city (max 100 characters)"
          maxLength={100}
        />
        <div className={styles.characterCount}>
          {formData.city.length}/100 characters
        </div>
      </div>

      {/* New wrapper for Latitude and Longitude */}
      <div className={styles.latLongRow}>
        <div className={styles.textfiledRoomLongitude}>
          <div className={styles.roomLocation1}>Room Location Longitude</div>
          <input
            className={styles.longitudeInput}
            value={formData.longitude}
            onChange={(e) => handleInputChange("longitude", e.target.value)}
            placeholder="Enter longitude (e.g., 25.240373)"
            type="number"
            step="0.000001"
          />
        </div>

        <div className={styles.textfiledRoomLatitude}>
          <div className={styles.roomLocation2}>Room Location Latitude</div>
          <input
            className={styles.latitudeInput}
            value={formData.latitude}
            onChange={(e) => handleInputChange("latitude", e.target.value)}
            placeholder="Enter latitude (e.g., 55.570952)"
            type="number"
            step="0.000001"
          />
        </div>
      </div>

      <div style={{ border: "1px solid #e6e6e6", borderRadius: 10, padding: 12, marginTop: 12, background: "#f9f9f9" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
          <input
            type="checkbox"
            checked={formData.geoRestricted}
            onChange={(e) => handleInputChange("geoRestricted", e.target.checked)}
          />
          Restrict listener access by location
        </label>
        <div style={{ fontSize: 12, color: "#555", marginTop: 6 }}>
          Uses the Room Location latitude/longitude above as the geofence center. Listeners must be inside the radius to join.
        </div>

        {formData.geoRestricted && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginTop: 12 }}>
            <div>
              <div className={styles.roomName}>Radius (meters)</div>
              <input
                className={styles.longitudeInput}
                value={formData.geoRadiusMeters}
                onChange={(e) => handleInputChange("geoRadiusMeters", e.target.value)}
                placeholder="e.g., 300"
                type="number"
                min={10}
                max={100000}
              />
            </div>
            <div>
              <div className={styles.roomName}>Location label (optional)</div>
              <input
                className={styles.roomCityInput}
                value={formData.geoLabel}
                onChange={(e) => handleInputChange("geoLabel", e.target.value)}
                placeholder="Dubai Mall entrance"
                maxLength={255}
              />
            </div>
            <div>
              <div className={styles.roomName}>Geofence center</div>
              <div style={{ fontSize: 13, color: "#333", padding: "8px 10px", border: "1px solid #ddd", borderRadius: 6, background: "#fff" }}>
                {formData.latitude && formData.longitude
                  ? `${formData.latitude}, ${formData.longitude}`
                  : "Set Room Location latitude/longitude above"}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className={styles.checkboxEnableTranscription}> {/* Ensure this class exists or is styled */}
        <input
          type="checkbox"
          id="enableTranscription"
          className={styles.enableTranscriptionCheckbox}
          checked={formData.enableTranscription}
          onChange={(e) => handleInputChange("enableTranscription", e.target.checked)}
        />
        <label htmlFor="enableTranscription" className={styles.checkboxLabel}>
          Enable Transcription
        </label>
      </div>

      <div className={styles.checkboxShowTranslationText}> {/* New show translation text checkbox */}
        <input
          type="checkbox"
          id="showTranslationText"
          className={styles.showTranslationTextCheckbox}
          checked={formData.showTranslationText}
          onChange={(e) => handleInputChange("showTranslationText", e.target.checked)}
        />
        <label htmlFor="showTranslationText" className={styles.checkboxLabel}>
          Show Translation Text
        </label>
      </div>      <div className={styles.dropdownSpeakerLanguage}>
        <div className={styles.roomName}>Speaker Language</div>
        <div style={{ position: 'relative' }}>
          <select
            className={styles.speakerLanguageSelect}
            value={formData.speakerLanguage}
            onChange={(e) => handleInputChange("speakerLanguage", e.target.value)}
          >
            {languageOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <img
            className={styles.arrowDropDownIcon}
            alt=""
            src="/arrow-drop-down.svg"
          />
        </div>
      </div>
      <div className={styles.dropdownListenersLanguages}>
        <div className={styles.roomName}>Listeners Languages</div>
        <div className={styles.multiselectDropdown} ref={dropdownRef}>
          <div
            className={styles.multiselectTrigger}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            <span className={styles.selectedText}>{getSelectedLanguagesText()}</span>
            <img
              className={`${styles.arrowDropDownIcon} ${isDropdownOpen ? styles.arrowRotated : ''}`}
              alt=""
              src="/arrow-drop-down.svg"
            />
          </div>
          {isDropdownOpen && (<div className={styles.checkboxGroup}>
            {languageOptions.map(option => {
              let selectedLanguages = formData.listenersLanguages || [];

              // Handle case where listenersLanguages is a string representation of an array
              if (typeof selectedLanguages === 'string') {
                try {
                  selectedLanguages = JSON.parse(selectedLanguages);
                } catch (e) {
                  selectedLanguages = [selectedLanguages];
                }
              }

              // Ensure it's an array
              if (!Array.isArray(selectedLanguages)) {
                selectedLanguages = [];
              }

              const isChecked = selectedLanguages.includes(option.value);

              return (
                <label key={option.value} className={styles.checkboxItem}>
                  <input
                    type="checkbox"
                    className={styles.languageCheckbox}
                    checked={isChecked}
                    onChange={(e) => handleListenersLanguagesChange(option.value, e.target.checked)}
                  />
                  <span className={styles.checkboxLabel}>{option.label}</span>
                </label>
              );
            })}
          </div>
          )}
        </div>
        <div className={styles.multiselectHint}>Select one or more languages for listeners</div>
      </div>

      {/* Scheduling Section */}
      <div className={styles.schedulingSection}>
        <div className={styles.sectionHeader}>Scheduling</div>
        <div className={styles.scheduleRow}>
          <div className={styles.scheduleField}>
            <label className={styles.roomName}>Schedule Mode</label>
            <select
              className={styles.scheduleSelect}
              value={formData.scheduleActive}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                // Auto mode sets Friday and clears times
                if (val === 2) {
                  handleInputChange("scheduleDayOfWeek", 5);
                  handleInputChange("scheduleStartTime", "");
                  handleInputChange("scheduleEndTime", "");
                }
                handleInputChange("scheduleActive", val);
              }}
            >
              <option value={0}>Off</option>
              <option value={1}>Manual Schedule</option>
              <option value={2}>Automatic Friday Sermon</option>
            </select>
          </div>
        </div>
        {formData.scheduleActive === 1 && (
          <div className={styles.manualScheduleFields}>
            <div className={styles.scheduleRow}>
              <div className={styles.scheduleField}>
                <label className={styles.roomName}>Day of Week</label>
                <select
                  className={styles.scheduleSelect}
                  value={formData.scheduleDayOfWeek}
                  onChange={(e) => handleInputChange("scheduleDayOfWeek", parseInt(e.target.value, 10))}
                >
                  <option value={0}>Sunday</option>
                  <option value={1}>Monday</option>
                  <option value={2}>Tuesday</option>
                  <option value={3}>Wednesday</option>
                  <option value={4}>Thursday</option>
                  <option value={5}>Friday</option>
                </select>
              </div>
              <div className={styles.scheduleField}>
                <label className={styles.roomName}>Start Time</label>
                <input
                  type="time"
                  className={styles.scheduleTimeInput}
                  value={formData.scheduleStartTime}
                  onChange={(e) => handleInputChange("scheduleStartTime", e.target.value)}
                />
              </div>
              <div className={styles.scheduleField}>
                <label className={styles.roomName}>End Time</label>
                <input
                  type="time"
                  className={styles.scheduleTimeInput}
                  value={formData.scheduleEndTime}
                  onChange={(e) => handleInputChange("scheduleEndTime", e.target.value)}
                />
              </div>
            </div>
            <div className={styles.scheduleHint}>Specify the day and time window in which the room should be active.</div>
          </div>
        )}
        {formData.scheduleActive === 2 && (
          <div className={styles.automaticInfo}>
            <div>Automatic Friday sermon scheduling is enabled.</div>
            <div className={styles.nextScheduleStatus}>
              {nextScheduleLoading && <span>Loading next schedule...</span>}
              {!nextScheduleLoading && nextScheduleError && (
                <span className={styles.nextScheduleError}>Error: {nextScheduleError}</span>
              )}
              {!nextScheduleLoading && !nextScheduleError && nextScheduleLocal && (
                <span className={styles.nextScheduleSuccess}>Next schedule: {nextScheduleLocal}</span>
              )}
              {!nextScheduleLoading && !nextScheduleError && !nextScheduleLocal && (
                <span className={styles.nextScheduleNoData}>No upcoming schedule available.</span>
              )}
            </div>
            {nextScheduleReference && (
              <div className={styles.nextScheduleReference}>Reference (Dhuhr {nextScheduleReference?.dhuhr}, {nextScheduleReference?.city}, {nextScheduleReference?.country}, {nextScheduleReference?.date})</div>
            )}
          </div>
        )}
      </div>

      {/* Access control passwords */}
      <PasswordFieldGroup formData={formData} handleInputChange={handleInputChange} />

      <div className={styles.saveSettingsButtonWrapper}>
        {showDelete && roomId != null && (
          <button
            type="button"
            className={styles.deleteButton}
            onClick={onDeleteClick}
            disabled={isSaving || isDeleting}
            title="Delete this room"
          >
            {isDeleting ? 'Deleting…' : 'Delete Room'}
          </button>
        )}
        <button
          className={styles.saveSettingsButton} onClick={handleSubmit}
          disabled={isSaving}
        >
          {isSaving ? (
            <div className={styles.loadingSpinnerSmall} />
          ) : (
            <span className={styles.saveSettings}>Save Settings</span>
          )}
        </button>
        <button
          type="button"
          className={styles.cancelButton}
          onClick={onCancelClick}
          disabled={isSaving || isDeleting}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default BasicSetupScreen;

// Password UI component placed after main export to keep file organized
const PasswordFieldGroup: FunctionComponent<{
  formData: any;
  handleInputChange: (field: string, value: string | boolean | string[] | number | null) => void;
}> = ({ formData, handleInputChange }) => {
  const [showSpeaker, setShowSpeaker] = useState(false);
  const [showListener, setShowListener] = useState(false);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // Small visual feedback could be added
    } catch (e) {
      console.error('Copy failed', e);
    }
  };

  const masked = (val: string) => (val ? '•'.repeat(8) : '');

  return (
    <div className={styles.passwordGroup}>
      <div className={styles.passwordField}>
        <label className={styles.roomName}>Speaker Password</label>
        <div className={styles.passwordRow}>
          <input
            type={showSpeaker ? 'text' : 'password'}
            value={formData.speakerPassword || ''}
            onChange={(e) => handleInputChange('speakerPassword', e.target.value)}
            placeholder="Leave empty for public"
            className={styles.passwordInput}
          />
          <button type="button" className={styles.passwordIconButton} onClick={() => copyToClipboard(formData.speakerPassword || '')} title="Copy Speaker Password" aria-label="Copy Speaker Password">📋</button>
          <button type="button" className={styles.passwordIconButton} onClick={() => setShowSpeaker(s => !s)} title="Toggle visibility" aria-label="Toggle Speaker Password Visibility">{showSpeaker ? '🙈' : '👁️'}</button>
        </div>
      </div>

      <div className={styles.passwordField}>
        <label className={styles.roomName}>Listeners Password</label>
        <div className={styles.passwordRow}>
          <input
            type={showListener ? 'text' : 'password'}
            value={formData.listenersPassword || ''}
            onChange={(e) => handleInputChange('listenersPassword', e.target.value)}
            placeholder="Leave empty for public"
            className={styles.passwordInput}
          />
          <button type="button" className={styles.passwordIconButton} onClick={() => copyToClipboard(formData.listenersPassword || '')} title="Copy Listeners Password" aria-label="Copy Listeners Password">📋</button>
          <button type="button" className={styles.passwordIconButton} onClick={() => setShowListener(s => !s)} title="Toggle visibility" aria-label="Toggle Listeners Password Visibility">{showListener ? '🙈' : '👁️'}</button>
        </div>
      </div>
    </div>
  );
};
